import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle } from 'react-icons/fi';
import ThemeToggle from '../ThemeToggle';
import { ThemeContext } from '../../context/ThemeContext';
import ProductPreview from '../marketing/ProductPreview';

const CHECKS = [
  'Prioridad del día: a quién cobrar primero',
  'Pack mensual listo para tu contador',
  '30 días de prueba · sin facturación ARCA',
];

const CONTENT_MAX = {
  default: 'max-w-md',
  form: 'max-w-xl',
  signup: 'max-w-3xl',
  wide: 'max-w-5xl',
  plan: 'max-w-[min(1200px,100%)]',
  extra: 'max-w-6xl xl:max-w-7xl',
};

export default function AuthSplitShell({
  children,
  footer,
  focusForm = false,
  hideBrand = false,
  wide = false,
  extraWide = false,
  planPicker = false,
  contentWidth,
  centerVertically,
  dense = false,
}) {
  const { modoOscuro, setModoOscuro } = useContext(ThemeContext);
  const pageBg = modoOscuro ? 'bg-black text-white' : 'bg-[#f5f5f7] text-gray-900';
  const brandBg = modoOscuro ? 'bg-[#0a0a0a] border-gray-800' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-white/80' : 'text-black/80';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const logoSrc = modoOscuro ? '/logowhite.png' : '/logoblack.png';

  let maxWidthKey = contentWidth;
  if (!maxWidthKey) {
    if (planPicker) maxWidthKey = 'plan';
    else if (extraWide) maxWidthKey = 'extra';
    else if (wide) maxWidthKey = 'wide';
    else maxWidthKey = 'default';
  }
  const maxWidthClass = CONTENT_MAX[maxWidthKey] || CONTENT_MAX.default;

  const themeBtn = (
    <ThemeToggle
      modoOscuro={modoOscuro}
      onToggle={setModoOscuro}
      appearance={modoOscuro ? 'authDark' : 'authLight'}
      size="md"
    />
  );

  const showAside = !hideBrand && !planPicker && !focusForm;
  const shouldCenter =
    centerVertically ?? (showAside && !focusForm && !planPicker);
  const footerInCenter = Boolean(footer && shouldCenter);

  return (
    <div className={`min-h-[100svh] w-full flex ${pageBg}`}>
      {showAside && (
        <aside className={`hidden lg:flex lg:w-[44%] xl:w-[46%] flex-col h-[100svh] border-r overflow-hidden p-6 xl:p-8 ${brandBg}`}>
          <Link to="/" className="inline-flex w-fit shrink-0">
            <img src={logoSrc} alt="Contsuite" className="h-8 w-auto" />
          </Link>

          <div className="flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex items-center py-4">
            <div className="max-w-lg w-full space-y-4 mx-auto">
              <div>
                <p className={`text-[11px] font-semibold tracking-[0.18em] uppercase mb-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                  Cobranzas inteligentes para pymes
                </p>
                <h1 className={`text-2xl xl:text-3xl font-extrabold leading-[1.1] tracking-tight ${textPri}`}>
                  Cobrá más rápido. Cerrá el mes con tu contador.
                </h1>
                <p className={`mt-2 text-sm leading-relaxed ${textSec}`}>
                  Prioridad del día, recordatorios automáticos y pack mensual para el estudio. 100% web.
                </p>
              </div>

              <ul className="space-y-1.5">
                {CHECKS.map((item) => (
                  <li key={item} className={`flex items-start gap-2.5 text-sm ${textSec}`}>
                    <FiCheckCircle className={`w-4 h-4 mt-0.5 shrink-0 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
                    {item}
                  </li>
                ))}
              </ul>

              <ProductPreview variant="panel" idPrefix="auth-preview" className="max-w-md w-full" />
            </div>
          </div>

          <p className={`text-[11px] shrink-0 pt-3 ${textMuted}`}>
            © {new Date().getFullYear()} Contsuite. Cobranzas y registro para pymes — sin facturación ARCA.
          </p>
        </aside>
      )}

      <main className={`flex-1 flex flex-col min-h-0 h-[100svh] min-w-0 ${planPicker || focusForm ? 'w-full' : ''}`}>
        <header
          className={`flex items-center justify-between px-4 sm:px-6 shrink-0 max-w-full ${
            dense ? 'py-2' : focusForm ? 'py-2.5' : 'py-4'
          }`}
        >
          <Link to="/" className={showAside ? 'inline-flex lg:hidden' : 'inline-flex'}>
            <img src={logoSrc} alt="Contsuite" className="h-7 w-auto" />
          </Link>
          <div className="ml-auto">{themeBtn}</div>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
          <div
            className={`flex flex-col px-4 sm:px-6 ${
              shouldCenter
                ? 'min-h-full justify-center py-5 sm:py-6'
                : footer
                  ? dense
                    ? 'py-2 sm:py-3 pb-2'
                    : 'py-3 sm:py-4 pb-3'
                  : 'py-4 sm:py-6 pb-16 sm:pb-20'
            }`}
          >
            <div className={`w-full ${maxWidthClass} mx-auto`}>
              {children}
              {footerInCenter ? <div className="mt-5 sm:mt-6">{footer}</div> : null}
            </div>
          </div>
        </div>

        {footer && !footerInCenter ? (
          <div
            className={`shrink-0 border-t px-4 sm:px-6 ${dense ? 'py-2' : 'py-3 sm:py-3.5'} backdrop-blur-md ${
              modoOscuro ? 'border-gray-800 bg-black/85' : 'border-gray-200/80 bg-[#f5f5f7]/90'
            }`}
          >
            <div className={`w-full ${maxWidthClass} mx-auto`}>{footer}</div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
